import { useCallback, useRef, useState } from 'react'

import { NextPage } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { chatGetOrCreateChatSpace } from '~/api-client/food_api/chat'
import { useUserSearchUsers } from '~/api-client/food_api/user'
import { AppImage } from '~/components/data-display/AppImage'
import { TextField } from '~/components/data-input/TextField'
import { ChevronLeftIcon, DeleteIcon, SearchIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { userProfileIcon } from '~/libs/user'
import { useSession } from '~/provider/SessionProvider'

type Props = {}

const Page: NextPage<Props> = (props) => {
  const router = useRouter()
  const session = useSession({ loginRequired: true })
  const accessToken = session.response?.data?.accessToken
  const [searchQuery, setSearchQuery] = useState('')

  const searchQueryInputRef = useRef<HTMLInputElement | null>(null)
  const clearSearchQuery = useCallback(() => {
    if (searchQueryInputRef.current) {
      searchQueryInputRef.current.value = ''
      setSearchQuery('')
    }
  }, [])

  const onChangeSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  const searchUsers = useUserSearchUsers({ key: searchQuery })

  return (
    <div className="min-h-full">
      <Head>
        <title>
          {router.query.gift ? 'ギフト送付先選択' : '個別チャットを作成'}
        </title>
      </Head>

      <Header>
        <ChevronLeftIcon fill="#fff" onClick={() => router.back()} />
        {router.query.gift ? 'ギフト送付先選択' : '個別チャットを作成'}
        <div />
      </Header>

      <Container>
        <ContainerInset>
          <div className={'flex gap-x-2 items-center'}>
            <div className="flex-grow">
              <TextField
                leftIcon={<SearchIcon className="w-4 h-4 fill-text" />}
                inputProps={{
                  placeholder: '名前、ユーザーIDから検索する',
                  onChange: onChangeSearchQuery,
                  ref: searchQueryInputRef,
                }}
              />
            </div>
            {searchQuery && (
              <button onClick={clearSearchQuery}>
                <DeleteIcon className="fill-text w-5 h-5" />
              </button>
            )}
          </div>

          {router.query.gift &&
            !searchUsers.isLoading &&
            searchUsers.data == null && (
              <div className="text-[13px] flex flex-col gap-3 mt-4">
                ギフトを贈りたいユーザーを検索して、選択をすると個別メッセージが開きます。
                <div className="relative w-[345px] max-w-[100%]">
                  <div
                    className="absolute rounded-full border-[3px]  border-[#AD3F4C] top-[50%] -translate-y-[50%] z-10 aspect-square"
                    style={{
                      left: 'calc(33 / 345 * 100%)',
                      width: 'calc(38 / 345 * 100%)',
                    }}
                  />
                  <Image
                    src="/images/gift_screenshot.png"
                    width={690}
                    height={127}
                    alt=""
                  />
                </div>
                個別メッセージの左下のプレゼントボックスのアイコンをタップすると、相手にギフトを贈ることができます。
              </div>
            )}
        </ContainerInset>

        <ContainerInset>
          {searchUsers.data?.users
            ?.filter((user) => user.userId !== session.response?.data.userId)
            .map((user) => {
              return (
                <div key={user.userId} className="mb-4">
                  <button
                    className="w-full"
                    onClick={async () => {
                      if (accessToken != null) {
                        const chatSpace = await chatGetOrCreateChatSpace({
                          accessToken,
                          pairUserId: user.userId,
                        })
                        await router.push(
                          `/chat_spaces/${chatSpace.space.chatSpaceId}`
                        )
                      }
                    }}
                    disabled={session.response?.data?.accessToken == null}
                  >
                    <div className="flex gap-4">
                      <div className="min-w-[54px] relative">
                        <AppImage
                          variant={{
                            type: 'profile',
                            user: user,
                          }}
                          rounded="full"
                          width={45}
                          height={45}
                          className="rounded-full"
                          alt={`${user.displayName}さんのプロフィール画像`}
                          src={userProfileIcon(user)}
                        />
                      </div>
                      <div className="flex-grow flex-shrink min-w-0 flex items-center justify-between gap-x-1 text-[13px]">
                        <div className="min-w-0">
                          <div className="truncate text-left">
                            {user.displayName}
                          </div>
                          <div className="truncate font-normal text-[#6C6C6C] text-xs mt-1 text-left">
                            @{user.userName}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              )
            })}
        </ContainerInset>
      </Container>
      <Footer />
    </div>
  )
}

export default Page
